import React, { useMemo, useState, JSX } from 'react';
import { Button } from 'reactstrap';
import { useTheme } from '@emotion/react';
import { faMapMarkerAlt, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { itemDetailEvent, sanitize } from '@ibe/services';
import { useMount } from 'react-use';
import { ApiBaseData, ApiBestPricePackageModel, ApiImageFromJSON } from '@ibe/api';
import fallback from '../../../_Translations/generated/package-details.en.t.json';
import Keys from '../../../_Translations/generated/package-details.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import ContentContainer from '../../Components/ContentContainer/ContentContainer';
import PictureGallery from '../../Components/PictureGallery/PictureGallery';
import DateDisplay from '../../Components/UI/DateDisplay/DateDisplay';
import { splitMediaIntoImagesAndVideos } from '../../Components/UI/Slider/Slider';
import ModalLayer from '../../Components/ModalLayer/ModalLayer';
import { PackageDetailsSelection } from './PackageDetails';
import ScrollerService from '../../Services/ScrollerService';
import useConfig from '../../Hooks/useConfig';
import PackageInfo from './PackageInfo';
import StarRating from '../../Components/UI/StarRating/StarRating';
import useWindow from '../../Hooks/useWindow';
import PackageSearchParams from '../PackageSearch/PackageSearchParams';

export interface PackageBestPriceDetailsProps {
  goBackToList: () => void;
  item: ApiBestPricePackageModel;
  showMealDetails?: boolean;
  hideDetailBackButton?: boolean;
  showCharacteristics?: boolean;
  onSelect?: (s: PackageDetailsSelection) => void;
  ExternalBestPriceDetailsContent?: {
    MainContent: (props: {
      item: ApiBestPricePackageModel;
      searchParams: PackageSearchParams;
    }) => JSX.Element;
    PriceDetails?: (props: {
      item: ApiBestPricePackageModel;
      searchParams: PackageSearchParams;
    }) => JSX.Element;
  };
  bestPriceListSelectionsMode?: boolean;
  withRoomCountSelector?: boolean;
}

const PackageBestPriceDetails = (props: PackageBestPriceDetailsProps): JSX.Element => {
  const {
    goBackToList,
    showMealDetails,
    showCharacteristics,
    hideDetailBackButton,
    item: {
      price,
      name,
      characteristics,
      rooms,
      media,
      duration,
      description,
      geoAssignment,
      startDate,
      endDate,
      originName,
      options,
      category
    }
  } = props;
  const window = useWindow();
  const { t } = useTranslation('package-details', fallback, Keys);
  const ImageGallery = React.lazy(() => import('react-image-gallery'));

  const config = useConfig();
  const {
    placeholderImages: { package: placeHolderImage }
  } = useTheme();

  const [initialGalleryImageIndex, setInitialGalleryImageIndex] = useState<number | undefined>(
    undefined
  );

  const stars = category ? Number(category.description || '0') : 0;

  useMount(() => {
    itemDetailEvent.broadcast({ item: props.item });

    setTimeout(() => {
      if (config.outsideElementsContainerId) {
        ScrollerService.scrollTo(config.outsideElementsContainerId, { offset: -100 });
      } else {
        window?.scrollTo({ top: 0, left: 0 });
      }
    }, 10);
  });

  const defaultRoom = rooms?.find(room => room.isDefault);

  const defaultMealDescription =
    defaultRoom && defaultRoom.defaultMeal ? defaultRoom.defaultMeal.description : t(Keys.roomOnly);

  const galleryImages = useMemo(() => {
    const images = [...splitMediaIntoImagesAndVideos(media).images];
    return images.length > 0 ? images : [ApiImageFromJSON({ url: placeHolderImage })];
  }, [media, placeHolderImage]);

  const sortedCharacteristics = useMemo(() => {
    const sorted: { [key: string]: string[] } = {};
    if (showCharacteristics && characteristics && characteristics.length > 0) {
      characteristics.forEach((element: ApiBaseData) => {
        if (element.group) {
          sorted[element.group] = [...(sorted[element.group] || []), element.description];
        }
      });
    }
    return sorted;
  }, [characteristics]);

  const scrollToOffset = (elementId: string, offset: number) => {
    const element = document.getElementById(elementId);
    if (element) {
      const rect = element.getBoundingClientRect();
      const scrollY = typeof window !== 'undefined' && window !== null ? window.scrollY : 0;
      const scrollPosition = rect.top + scrollY - offset;
      window?.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleButtonClick = (): void => {
    // offset value must match the position(top) in the class passed as an argument in the function scrollToOffset
    let offset = 91;
    if (typeof window !== 'undefined' && window !== null && window.innerWidth >= 1200) {
      offset = 133;
    }
    scrollToOffset('iso__best-price-availability', offset);
  };

  const toggleModal = (): void => {
    setInitialGalleryImageIndex(value => (!!value || value === 0 ? undefined : 0));
  };

  return (
    <div className="bestprice-details">
      {!hideDetailBackButton && (
        <Button className="mb-4" onClick={(): void => goBackToList()}>
          {' '}
          {t(Keys.BACK_TO_LIST)}
        </Button>
      )}
      <ContentContainer>
        <div className="d-flex justify-content-between flex-column flex-sm-row align-items-start mb-2">
          <div className="align-items-stretch mb-4 mb-sm-0">
            <h1 className="bestprice-details__headline">
              {name}&nbsp;
              <StarRating numberOfStars={stars} />
            </h1>
            <div className="bestprice-details__infos">
              <div>
                <span className="bestprice-details__infos__icon">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </span>
                <span>{geoAssignment?.location || ''}</span>
              </div>
              <div>
                <span className="bestprice-details__infos__icon">
                  <FontAwesomeIcon icon={faCalendar} />
                </span>
                <span>{t(Keys.nights, { duration: duration.duration })}</span>
                <span className="text-primary">{' | '}</span>
                <span className="bestprice-details__duration">
                  <DateDisplay date={startDate} /> - <DateDisplay date={endDate} />
                </span>
              </div>
            </div>
          </div>
          <PackageInfo
            price={price}
            board={defaultRoom?.description}
            room={defaultMealDescription}
            origin={originName}
            className="package-info__details"
          />
        </div>
        <PictureGallery
          images={galleryImages}
          onImageClick={(imageIndex: number): void => {
            setInitialGalleryImageIndex(imageIndex);
          }}
        />
        <div className="my-4 d-flex flex-column flex-sm-row align-items-start">
          <div className="flex-grow-1 order-1">
            <div className="collapse-headline mb-4">
              <h2 className="font-weight-bold">{t(Keys.yourHotel)}</h2>
              <div className="collapse-headline__separator mt-1" />
            </div>
            {showCharacteristics && Object.keys(sortedCharacteristics).length > 0 && (
              <>
                {Object.keys(sortedCharacteristics)
                  .sort((a, b) => a.localeCompare(b))
                  .map(key => (
                    <div key={key} className="mb-4">
                      <div className="font-weight-bold">{key}:</div>
                      <div>{sortedCharacteristics[key].join(', ')}</div>
                    </div>
                  ))}
              </>
            )}
            {showMealDetails && options.length > 0 && (
              <div className="mb-4">
                <div className="font-weight-bold">{t(Keys.meals)}:</div>
                <div>
                  {options
                    .map(meal => (!meal.code ? t(Keys.roomOnly) : meal.description))
                    .join(', ')}
                </div>
              </div>
            )}
            <div
              className="mt-4 magnoliaRichText"
              dangerouslySetInnerHTML={{ __html: sanitize(description || '') }}
            />
          </div>
          <Button
            className="bestprice-details__dates-button mb-3 mb-sm-0 ml-sm-4 order-0 order-sm-2"
            color="primary"
            onClick={handleButtonClick}
          >
            {t(Keys.datesAndPrices)}
          </Button>
        </div>
      </ContentContainer>
      <ModalLayer
        isOpen={initialGalleryImageIndex !== undefined}
        toggle={toggleModal}
        closeOnBackdrop
      >
        <ImageGallery
          items={galleryImages.map(image => ({
            original: image.url,
            thumbnail: image.url,
            originalAlt: image.altText,
            thumbnailAlt: image.altText
          }))}
          showFullscreenButton={false}
          showPlayButton={false}
          startIndex={initialGalleryImageIndex}
          thumbnailPosition="bottom"
        />
      </ModalLayer>
    </div>
  );
};

export default PackageBestPriceDetails;
